import { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Box,
  Card,
  useTheme,
  Avatar,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Fade
} from '@mui/material';
import { getApi, patchApi } from 'views/services/api';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LoopIcon from '@mui/icons-material/Loop';
import SingleBedIcon from '@mui/icons-material/SingleBed';
import KingBedIcon from '@mui/icons-material/KingBed';
import { toast } from 'react-toastify';
import axios from 'axios';

// Mapeo de iconos por tipo de habitación
const RoomTypeIcons = {
  single: <SingleBedIcon sx={{ fontSize: '2.5rem' }} />,
  double: <KingBedIcon sx={{ fontSize: '2.5rem' }} />,
  triple: <KingBedIcon sx={{ fontSize: '2.5rem' }} />,
  family: <KingBedIcon sx={{ fontSize: '2.5rem' }} />
};

// Colores de estado de limpieza
const getHousekeepingColors = (status, theme) => {
  switch (status) {
    case 'dirty':
      return {
        background: theme.palette.warning.light,
        border: theme.palette.warning.main,
        text: theme.palette.common.black,
        chip: 'warning'
      };
    case 'in-progress':
      return {
        background: theme.palette.info.lighter,
        border: theme.palette.info.light,
        text: theme.palette.info.dark,
        chip: 'info'
      };
    case 'clean':
      return {
        background: theme.palette.success.lighter,
        border: theme.palette.success.light,
        text: theme.palette.success.dark,
        chip: 'success'
      };
    default:
      return {
        background: theme.palette.warning.light,
        border: theme.palette.warning.main,
        text: theme.palette.common.black,
        chip: 'warning'
      };
  }
};

const HousekeepingPanel = () => {
  const theme = useTheme();
  const [roomData, setRoomData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const hotel = JSON.parse(localStorage.getItem('hotelData'));

  // Obtener datos de habitaciones
  const fetchRoomData = async () => {
    try {
      setLoading(true);
      const response = await getApi(`api/room/viewallrooms/${hotel?.hotelId}`);
      if (response && response.data) {
        // Filtrar solo habitaciones sucias
        const dirtyRooms = response.data.filter((room) => room.housekeepingStatus === 'dirty' || room.housekeepingStatus === 'in-progress');
        setRoomData(dirtyRooms);
      }
    } catch (error) {
      console.error('Error al obtener datos de habitaciones:', error);
      toast.error('No se pudieron cargar los datos de limpieza');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoomData();

    // Configurar un intervalo para actualizar los datos cada minuto
    const interval = setInterval(() => {
      fetchRoomData();
    }, 60000); // 60 segundos

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRoomClick = (room) => {
    setSelectedRoom(room);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // Usando el método que es consistente con EditRoom.js
  const updateRoomStatus = async (newStatus) => {
    if (!selectedRoom) return;

    try {
      console.log(`Actualizando estado de habitación ${selectedRoom.roomNo} a ${newStatus}`);

      // Crear el objeto values exactamente como lo haría EditRoom.js
      const values = {
        ...selectedRoom,
        housekeepingStatus: newStatus
      };

      // Usar el mismo endpoint de API que usa EditRoom.js
      const response = await patchApi(`api/room/edit/${selectedRoom._id}`, values);

      if (response && (response.status === 200 || response.data)) {
        console.log('API respondió exitosamente:', response);
        toast.success(`Habitación #${selectedRoom.roomNo} ahora está ${newStatus === 'clean' ? 'limpia' : 'en proceso de limpieza'}`);
        setDialogOpen(false);
        // Obtener los datos actualizados después de un breve retraso para permitir que el backend se actualice
        setTimeout(() => {
          fetchRoomData();
        }, 500);
      } else {
        console.error('API respondió con un error:', response);
        toast.error('No se pudo actualizar el estado de la habitación');
      }
    } catch (error) {
      console.error('Error al actualizar el estado de la habitación:', error);

      // Intentar un enfoque más con llamada directa a axios
      try {
        const token = localStorage.getItem('token');
        const response = await axios.patch(
          `/api/room/update/${selectedRoom._id}`,
          {
            housekeepingStatus: newStatus
          },
          {
            headers: {
              Authorization: token ? `Bearer ${token}` : '',
              'Content-Type': 'application/json'
            }
          }
        );

        if (response && response.data) {
          toast.success(`Habitación #${selectedRoom.roomNo} ahora está ${newStatus === 'clean' ? 'limpia' : 'en proceso de limpieza'}`);
          setDialogOpen(false);
          fetchRoomData();
        } else {
          toast.error('No se pudo actualizar el estado de la habitación');
        }
      } catch (secondError) {
        console.error('El segundo intento también falló:', secondError);
        toast.error('No se pudo actualizar el estado de la habitación. Por favor, inténtelo de nuevo.');
      }
    }
  };

  return (
    <>
      <Card sx={{ p: 2, boxShadow: theme.shadows[3], borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Limpieza
            </Typography>
            <Chip label={`${roomData?.length || 0} habitaciones necesitan atención`} size="small" color="warning" sx={{ ml: 1, mt: 0.5 }} />
          </Box>
          <Box>
            <Chip
              icon={<CleaningServicesIcon />}
              label="Sucia"
              color="warning"
              variant="outlined"
              size="small"
              sx={{ mr: 1, '& .MuiChip-label': { color: 'black', fontWeight: 600 } }}
            />
            <Chip icon={<LoopIcon />} label="En Proceso" color="info" variant="outlined" size="small" sx={{ mr: 1 }} />
            <Chip icon={<CheckCircleIcon />} label="Limpia" color="success" variant="outlined" size="small" />
          </Box>
        </Box>

        <Box
          sx={{
            height: '1px',
            backgroundColor: theme.palette.divider,
            width: '100%',
            my: 2
          }}
        />

        {loading ? (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item}>
                <Paper
                  sx={{
                    p: 2,
                    height: 120,
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.default
                  }}
                />
              </Grid>
            ))}
          </Grid>
        ) : roomData?.length > 0 ? (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {roomData.map((room) => {
              const housekeepingStatus = room.housekeepingStatus || 'dirty';
              const colors = getHousekeepingColors(housekeepingStatus, theme);

              return (
                <Grid item key={room._id} xs={12} sm={6} md={4} lg={3} sx={{ cursor: 'pointer' }} onClick={() => handleRoomClick(room)}>
                  <Fade in={true} timeout={500}>
                    <Paper
                      elevation={3}
                      sx={{
                        borderRadius: '12px',
                        backgroundColor: colors.background,
                        transition: 'all 0.3s ease',
                        border: `1px solid ${colors.border}`,
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: theme.shadows[6]
                        },
                        position: 'relative'
                      }}
                    >
                      {/* Indicador de estado en la esquina superior derecha */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          backgroundColor: colors.border,
                          color: housekeepingStatus === 'dirty' ? theme.palette.common.black : '#fff',
                          fontWeight: housekeepingStatus === 'dirty' ? 600 : 500,
                          px: 2,
                          py: 0.5,
                          borderBottomLeftRadius: '12px',
                          fontSize: '0.75rem',
                          zIndex: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        {housekeepingStatus === 'dirty' ? (
                          <>
                            <CleaningServicesIcon sx={{ fontSize: '0.875rem', mr: 0.5 }} />
                            Sucia
                          </>
                        ) : housekeepingStatus === 'in-progress' ? (
                          <>
                            <LoopIcon sx={{ fontSize: '0.875rem', mr: 0.5 }} />
                            En Proceso
                          </>
                        ) : (
                          <>
                            <CheckCircleIcon sx={{ fontSize: '0.875rem', mr: 0.5 }} />
                            Limpia
                          </>
                        )}
                      </Box>

                      <Box sx={{ p: 2.5 }}>
                        <Grid container alignItems="center" spacing={2}>
                          <Grid item>
                            <Avatar
                              sx={{
                                width: 60,
                                height: 60,
                                backgroundColor: colors.background,
                                color: colors.text
                              }}
                            >
                              {RoomTypeIcons[room.roomType?.toLowerCase()] || <KingBedIcon sx={{ fontSize: '2.5rem' }} />}
                            </Avatar>
                          </Grid>

                          <Grid item xs>
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 600,
                                color: colors.text
                              }}
                            >
                              Habitación #{room.roomNo}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                mt: 0.5,
                                color: colors.text,
                                textTransform: 'capitalize'
                              }}
                            >
                              {room.roomType === 'single'
                                ? 'Individual'
                                : room.roomType === 'double'
                                ? 'Doble'
                                : room.roomType === 'triple'
                                ? 'Triple'
                                : room.roomType === 'family'
                                ? 'Familiar'
                                : room.roomType || 'Estándar'}
                            </Typography>

                            <Box
                              sx={{
                                mt: 1,
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              <Chip
                                size="small"
                                label={
                                  housekeepingStatus === 'dirty'
                                    ? 'Necesita Limpieza'
                                    : housekeepingStatus === 'in-progress'
                                    ? 'Limpieza en Progreso'
                                    : 'Limpia'
                                }
                                color={colors.chip}
                                sx={{
                                  fontWeight: housekeepingStatus === 'dirty' ? 600 : 400,
                                  '& .MuiChip-label': {
                                    color: housekeepingStatus === 'dirty' ? 'black' : undefined,
                                    fontWeight: housekeepingStatus === 'dirty' ? 600 : undefined
                                  }
                                }}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Paper>
                  </Fade>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 60,
                color: theme.palette.success.main,
                opacity: 0.8,
                mb: 2
              }}
            />
            <Typography
              variant="h5"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500
              }}
            >
              Todas las Habitaciones Están Limpias
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.disabled,
                mt: 1
              }}
            >
              No hay habitaciones que requieran limpieza en este momento
            </Typography>
          </Box>
        )}
      </Card>

      {/* Diálogo Simplificado de Cambio de Estado */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>Actualizar Estado de Habitación</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              my: 2
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Habitación #{selectedRoom?.roomNo}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Estado Actual:{' '}
              <span
                style={{
                  fontWeight: 'bold',
                  color:
                    selectedRoom?.housekeepingStatus === 'dirty'
                      ? theme.palette.warning.dark
                      : selectedRoom?.housekeepingStatus === 'in-progress'
                      ? theme.palette.info.dark
                      : theme.palette.success.dark
                }}
              >
                {selectedRoom?.housekeepingStatus === 'dirty'
                  ? 'Sucia'
                  : selectedRoom?.housekeepingStatus === 'in-progress'
                  ? 'En Proceso'
                  : 'Limpia'}
              </span>
            </Typography>

            <Box sx={{ mt: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
              <Grid container spacing={2} justifyContent="center">
                {/* Tarjeta de Botón En Proceso */}
                <Grid item xs={5}>
                  <Paper
                    elevation={3}
                    onClick={() => updateRoomStatus('in-progress')}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: theme.palette.info.lighter,
                      border: `1px solid ${theme.palette.info.light}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: theme.shadows[5]
                      },
                      opacity: selectedRoom?.housekeepingStatus === 'in-progress' ? 0.6 : 1
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Avatar
                        sx={{
                          bgcolor: theme.palette.info.main,
                          width: 60,
                          height: 60,
                          mb: 1
                        }}
                      >
                        <LoopIcon sx={{ fontSize: '2rem', color: 'white' }} />
                      </Avatar>
                      <Typography variant="subtitle1" fontWeight={600} color="info.dark">
                        En Proceso
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        La habitación se está limpiando
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                {/* Tarjeta de Botón Limpia */}
                <Grid item xs={5}>
                  <Paper
                    elevation={3}
                    onClick={() => updateRoomStatus('clean')}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: theme.palette.success.lighter,
                      border: `1px solid ${theme.palette.success.light}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: theme.shadows[5]
                      },
                      opacity: selectedRoom?.housekeepingStatus === 'clean' ? 0.6 : 1
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Avatar
                        sx={{
                          bgcolor: theme.palette.success.main,
                          width: 60,
                          height: 60,
                          mb: 1
                        }}
                      >
                        <CheckCircleIcon sx={{ fontSize: '2rem', color: 'white' }} />
                      </Avatar>
                      <Typography variant="subtitle1" fontWeight={600} color="success.dark">
                        Limpia
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Habitación limpia y lista
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button onClick={handleCloseDialog} variant="outlined" color="inherit">
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HousekeepingPanel;
